import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonButton, IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { chevronUpSharp, chevronForwardSharp, chevronDownSharp, chevronBackSharp } from 'ionicons/icons';

import { CdkDragDrop, CdkDragRelease, CdkDragStart, DragDropModule } from '@angular/cdk/drag-drop';
import ImagePuzzleFile from '../interfaces/image-puzzle-file';
import { Md5 } from 'ts-md5/dist/md5';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { ImagePuzzleTile } from '../interfaces/image-puzzle-tile';
import { Store } from '@ngxs/store';
import { PuzzleGameState } from '../../store/puzzle-game.state';
import { ImagePuzzleGameState } from '../store/image-puzzle-game.state';
import { InitImagePuzzleGame, PersistImagePuzzleState } from '../store/image-puzzle-game.actions';
import { SetPuzzleID } from '../../store/puzzle-id.actions';
import { PuzzleGameTypesEnum } from '../../puzzle-framework-module/puzzle-game-types.enum';
import { SetFinished, SetGameProgress } from '../../store/puzzle-game.actions';
import { Subscription } from 'rxjs';
import { Button } from '../../shared-module/buttons/button.def';
import { HelperFunctionsService } from '../../shared-module/helpers/helper-functions.service';
import {
  PUZZLE_WIN_DIALOG_BUTTONS,
  PuzzleWinDialogService,
} from '../../shared-module/puzzle-win-dialog/puzzle-win-dialog.service';
import { ImagePuzzleWinScreenComponent } from '../image-puzzle-win-screen/image-puzzle-win-screen.component';
import { PuzzleHelpDialogService } from '../../shared-module/puzzle-help-dialog/puzzle-help-dialog.service';
import { ImagePuzzleHelpComponent } from '../image-puzzle-help/image-puzzle-help.component';
import { ImagePreviewData } from '../image-puzzle-preview/image-puzzle-preview.component';
import { ImagePuzzleGameModel } from '../store/image-puzzle-game.model';
import { PuzzleFrameworkEvents } from '../../puzzle-framework-module/puzzle-framework-events.enum';
import { ResizeObserver } from '@juggle/resize-observer';
import { PuzzleSettingsModalService } from '../../shared-module/puzzle-settings-modal/puzzle-settings-modal.service';

@Component({
  selector: 'lib-app-image-puzzle-board',
  templateUrl: './image-puzzle-board.component.html',
  styleUrls: ['./image-puzzle-board.component.scss'],
  standalone: true,
  imports: [CommonModule, IonButton, IonIcon, DragDropModule],
})
export class ImagePuzzleBoardComponent implements OnInit, AfterContentChecked, OnDestroy {
  @ViewChild('puzzleContainer', { static: true }) puzzleContainerRef: ElementRef;
  columns = 1;
  rows = 1;
  tiles: ImagePuzzleTile[] = [];
  tileImages: string[] = [];

  width: number;
  height: number;

  scaledHeight: number;
  scaledWidth: number;

  scaledTileHeight: number;
  scaledTileWidth: number;

  scaleValue: number;

  scale: string;
  clientRect: ClientRect;

  puzzleID: string;
  initialized = false;

  disableDrag = false;

  scaled = false;

  copyright: string;

  private buttonBarSubscription: Subscription;

  highlightFields = false;
  private highlightFieldsTimeout: number;

  hasTouchScreen = false;

  pointerDown: number;
  mouseMove: number;

  private winDialogSubscription: Subscription;
  private helpDialogSubscription: Subscription;

  shiftButtonType: string;
  private scaleTimeout: number;

  constructor(
    private framework: PuzzleFrameworkService,
    private el: ElementRef,
    private store: Store,
    private cd: ChangeDetectorRef,
    private helperService: HelperFunctionsService,
    private winDialogService: PuzzleWinDialogService,
    private helpDialogService: PuzzleHelpDialogService,
    private settingsModalService: PuzzleSettingsModalService
  ) {
    addIcons({
      chevronUpSharp,
      chevronForwardSharp,
      chevronDownSharp,
      chevronBackSharp,
    });

    if (this.helperService.unsupportedBrowser()) {
      this.framework.showBrowserWarningBadge();
    }

    this.winDialogSubscription = winDialogService.winDialogRegistered.subscribe(() => {
      const imagePuzzleConfig = this.framework.puzzleConfig;
      const data: ImagePreviewData = {
        tileImages: this.tileImages,
        rows: this.rows,
        columns: this.columns,
        height: this.height,
        width: this.width,
        description: imagePuzzleConfig.hasOwnProperty('description') ? imagePuzzleConfig.description : '',
      };

      winDialogService.injectComponent(ImagePuzzleWinScreenComponent, data);

      winDialogService.showButtons([PUZZLE_WIN_DIALOG_BUTTONS.RESTART_GAME, PUZZLE_WIN_DIALOG_BUTTONS.BACK_TO_GAME]);

      this.disableDrag = true;
    });

    this.helpDialogSubscription = helpDialogService.helpDialogRegistered.subscribe(() => {
      helpDialogService.injectHelpComponent(ImagePuzzleHelpComponent, {});
    });

    this.buttonBarSubscription = this.framework.buttonPressed.subscribe((button) => {
      switch (button) {
        case Button.Check:
          // console.log(button);
          this.triggerHighlightFields();
          break;
        case Button.Reset:
          this.resetPuzzle();
          break;
        case Button.ImageShiftRight:
          this.shiftRight();
          break;
        case Button.ImageShiftLeft:
          this.shiftLeft();
          break;
        case Button.ImageShiftUp:
          this.shiftUp();
          break;
        case Button.ImageShiftDown:
          this.shiftDown();
          break;
      }
    });

    this.framework.puzzleEvents.subscribe((event) => {
      switch (event) {
        case PuzzleFrameworkEvents.SOLVE_CURRENT_PUZZLE:
          this._winGame();
          break;
        case PuzzleFrameworkEvents.SOLVE_CURRENT_PUZZLE_BUT_ONE:
          this._winGame(1);
          break;
      }
    });

    this.hasTouchScreen = this.helperService.hasTouchScreen();
  }

  private async _winGame(leaveOut = 0) {
    if (leaveOut > 0) {
      leaveOut = Math.max(2, Math.min(leaveOut, this.tiles.length));
    }

    const randomIndices = this.tiles.map((tile) => tile.dropList[0].tileNumber);

    for (let x = 0; x < this.tiles.length; x++) {
      const i = randomIndices[x];

      let j = i;

      if (j < leaveOut) {
        j--;
        if (j < 0) {
          j = leaveOut - 1;
        }
      }

      if (this.checkForWin(false)) {
        break;
      }

      const correctTileIndex = this.tiles.map((tile) => tile.dropList[0].tileNumber).indexOf(j);

      const currentTile = this.tiles[i];
      this.tiles[i] = this.tiles[correctTileIndex];
      this.tiles[correctTileIndex] = currentTile;

      await this.helperService.timeout(20);
    }

    this.checkForWin();
  }

  ngOnDestroy(): void {
    this.winDialogSubscription.unsubscribe();
    this.buttonBarSubscription.unsubscribe();
    this.helpDialogSubscription.unsubscribe();
  }

  private triggerHighlightFields() {
    if (this.highlightFieldsTimeout) {
      clearTimeout(this.highlightFieldsTimeout);
    }
    this.highlightFields = true;
    this.highlightFieldsTimeout = window.setTimeout(() => {
      this.highlightFields = false;
    }, 1000);
  }

  private resetPuzzle() {
    this.disableDrag = false;
    const imageConfig = this.framework.puzzleConfig as ImagePuzzleFile;
    this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.ImagePuzzle, 0);
    const tiles = this.getTiles(imageConfig);
    this.tiles = this.shuffle(tiles);
    this.initImagePuzzle(this.puzzleID, this.tiles);
  }

  ngOnInit(): void {
    this.loadImagePuzzle();

    const observer = new ResizeObserver((entries) => {
      this.scaleImage();
    });
    observer.observe(this.el.nativeElement);
  }

  ngAfterContentChecked(): void {
    this.scaleImage();
  }

  onTileMouseDown(index: number) {
    this.pointerDown = index;
  }

  onTileMouseUp() {
    this.pointerDown = undefined;
  }

  onTileMouseMove(index: number) {
    if (this.mouseMove !== index) {
      this.mouseMove = index;
    }
  }

  onTileMouseLeave(index) {
    if (this.mouseMove === index) {
      this.mouseMove = undefined;
    }
  }

  public shuffle(array: ImagePuzzleTile[]) {
    array = this.helperService.randomizeArray(array);

    let iterationsLeft = 5;
    let perfect = false;

    while (iterationsLeft > 0 && !perfect) {
      let shiftAgain: { row: number; column: number }[] = [];
      // console.log(iterationsLeft, array.map(element => element.dropList[0].number));
      for (let row = 0; row < this.rows; row++) {
        for (let column = 0; column < this.columns; column++) {
          const thisTile = array[row * this.columns + column];
          if (thisTile.dropList[0].tileNumber === row * this.columns + column) {
            shiftAgain.push({ row, column });
            // console.log('wrong position', {row, column});
          } else {
            const checkHorizontally = column < this.columns - 1;
            const checkVertically = row < this.rows - 1;

            if (
              checkHorizontally &&
              array[row * this.columns + column + 1].dropList[0].tileNumber - thisTile.dropList[0].tileNumber === 1
            ) {
              // console.log('horizontally', {row, column});
              shiftAgain.push({ row, column });
            } else if (
              checkVertically &&
              array[(row + 1) * this.columns + column].dropList[0].tileNumber - thisTile.dropList[0].tileNumber ===
                this.columns
            ) {
              // console.log('vertically', {row, column});
              shiftAgain.push({ row, column });
            }
          }
        }
      }
      switch (shiftAgain.length) {
        case 0:
          perfect = true;
          break;
        case 1:
          // Switch with random element
          const shift = shiftAgain[0];
          let row = shift.row;
          let column = shift.column;
          while (shift.column === column && shift.row === row) {
            row = Math.floor(Math.random() * this.rows);
            column = Math.floor(Math.random() * this.columns);
          }
          const temp = array[shift.row * this.columns + shift.column];
          array[shift.row * this.columns + shift.column] = array[row * this.columns + column];
          array[row * this.columns + column] = temp;
          break;
        default:
          // Random rotate elements
          shiftAgain = this.helperService.randomizeArray(shiftAgain);
          const first = array[shiftAgain[0].row * this.columns + shiftAgain[0].column];
          for (let i = 0; i < shiftAgain.length - 1; i++) {
            array[shiftAgain[i].row * this.columns + shiftAgain[i].column] =
              array[shiftAgain[i + 1].row * this.columns + shiftAgain[i + 1].column];
          }
          array[shiftAgain[shiftAgain.length - 1].row * this.columns + shiftAgain[shiftAgain.length - 1].column] =
            first;
          break;
      }

      iterationsLeft--;
    }
    return array;
  }

  private getTiles(imageConfig: ImagePuzzleFile): ImagePuzzleTile[] {
    this.width = imageConfig.size.x;
    this.height = imageConfig.size.y;

    this.rows = imageConfig.slices.y;
    this.columns = imageConfig.slices.x;

    const tileCount = this.rows * this.columns;
    const tiles: ImagePuzzleTile[] = [];

    for (let i = 0; i < tileCount; i++) {
      tiles[i] = {
        dropList: [
          {
            tileNumber: i,
          },
        ],
      };
    }
    return tiles;
  }

  private getImages(imageConfig: ImagePuzzleFile) {
    return Object.values(imageConfig.image);
  }

  loadImagePuzzle() {
    const imageConfig = this.framework.puzzleConfig as ImagePuzzleFile;

    this.copyright = imageConfig.copyright;

    if (!this.checkPuzzleKey(imageConfig)) {
      return;
    }

    const tiles = this.getTiles(imageConfig);
    this.tiles = this.shuffle(tiles);

    this.tileImages = this.getImages(imageConfig);
    const puzzleID = imageConfig.activation_key;
    this.puzzleID = puzzleID;

    this.store.dispatch(new SetPuzzleID({ puzzleID }));

    this.store.select(PuzzleGameState.getByID(puzzleID)).subscribe((puzzleGame) => {
      if (puzzleGame === null) {
        this.framework.initPuzzleGameState(puzzleID, PuzzleGameTypesEnum.ImagePuzzle, 0);
        return;
      }
    });

    this.store.select(ImagePuzzleGameState.getByID(puzzleID)).subscribe((imagePuzzleGame: ImagePuzzleGameModel) => {
      if (imagePuzzleGame === null) {
        this.initImagePuzzle(puzzleID, this.tiles);
        return;
      }
      // migrate tiles from number to tileNumber. Can be removed in a few months
      this.tiles = JSON.parse(JSON.stringify(imagePuzzleGame.tiles)).map((tile) => ({
        dropList: tile.dropList.map((dropItem) => ({
          tileNumber: dropItem.tileNumber === undefined ? dropItem.number : dropItem.tileNumber,
        })),
      }));
      this.initialized = true;
      this.cd.detectChanges();
    });
  }

  private initImagePuzzle(puzzleID: string, tiles: ImagePuzzleTile[]) {
    this.store.dispatch(
      new InitImagePuzzleGame({
        puzzleID,
        tiles,
      })
    );
  }

  private persistImagePuzzleState() {
    this.store.dispatch(
      new PersistImagePuzzleState({
        puzzleID: this.puzzleID,
        tiles: this.tiles,
      })
    );
  }

  private getScaleValue(availableHeight: number, availableWidth: number) {
    return Math.min(availableHeight / this.height, availableWidth / this.width);
  }

  // @HostListener('window:resize')
  public scaleImage() {
    if (document.body.clientWidth === 0) {
      // since the ionic update, inactive browser tabs dont have a body width
      // as long as the tab is active, postpone the zoom operation
      if (this.scaleTimeout) {
        clearTimeout(this.scaleTimeout);
        this.scaleTimeout = undefined;
      }
      this.scaleTimeout = window.setTimeout(() => {
        this.scaleImage();
      }, 100);
      return;
    }

    const buttonDistance = 80;

    const availableHeight = this.el.nativeElement.offsetHeight - (this.copyright ? 20 : 0);
    const availableWidth = this.el.nativeElement.offsetWidth;

    const isMobile = this.helperService.isMobileDevice();

    const margin = 15;
    const marginSides = this.framework.puzzleFrameworkConfig.removeSideMargins ? 0 : margin;

    const maxScaleWithButtons = isMobile
      ? 0
      : this.getScaleValue(availableHeight - buttonDistance, availableWidth - buttonDistance);
    const maxScaleWithBottomBar = isMobile
      ? this.getScaleValue(availableHeight - margin - 140, availableWidth - marginSides)
      : 0;
    const maxScaleWithSideBar = isMobile
      ? this.getScaleValue(availableHeight - margin, availableWidth - margin - 140)
      : 0;

    const finalScale = Math.max(maxScaleWithButtons, maxScaleWithBottomBar, maxScaleWithSideBar);

    switch (finalScale) {
      case maxScaleWithButtons:
        this.shiftButtonType = 'borders';
        break;
      case maxScaleWithSideBar:
        this.shiftButtonType = 'side';
        break;
      case maxScaleWithBottomBar:
        this.shiftButtonType = 'bottom';
        break;
    }

    this.scaleValue = finalScale;
    this.scale = 'scale(' + this.scaleValue + ')';

    // Need rounding up, because on Firefox it might break the flex wrapping.
    this.scaledHeight = Math.ceil(this.height * this.scaleValue);
    this.scaledWidth = Math.ceil(this.width * this.scaleValue);

    this.scaledTileHeight = this.scaledHeight / this.rows;
    this.scaledTileWidth = this.scaledWidth / this.columns;

    const maxScaledDimension = Math.max(this.scaledTileHeight, this.scaledTileWidth);
    const hoverScaleValue = (0.02 / maxScaledDimension) * 250 + 1;
    const dragScaleValue = (0.05 / maxScaledDimension) * 250 + 1;

    document.documentElement.style.setProperty('--hover-scale-value', '' + hoverScaleValue);
    document.documentElement.style.setProperty('--drag-scale-value', '' + dragScaleValue);

    this.scaled = true;
  }

  private updateProgress() {
    let correct = 0;
    this.tiles.map((tile, index) => {
      if (index === tile.dropList[0].tileNumber) {
        correct++;
      }
    });
    this.store.dispatch(
      new SetGameProgress({
        puzzleID: this.puzzleID,
        progress: correct / this.tiles.length,
      })
    );
  }

  private checkForWin(persist = true) {
    if (persist) {
      this.framework.incrementMoveCounter(this.puzzleID);
      this.persistImagePuzzleState();
      this.updateProgress();
    }

    const indices = this.tiles.map((tile) => tile.dropList[0].tileNumber);
    let correct = 0;
    for (const index of indices) {
      if (correct !== index) {
        return false;
      }
      correct++;
    }

    if (persist) {
      this.store.dispatch(
        new SetFinished({
          puzzleID: this.puzzleID,
        })
      );
    }

    return true;
  }

  private checkPuzzleKey(imagePuzzleFile: ImagePuzzleFile) {
    return Md5.hashStr(JSON.stringify(imagePuzzleFile.image)) === imagePuzzleFile.activation_key;
  }

  onDragStart(event: CdkDragStart, index: number) {
    this.disableDrag = true;
  }

  onDragRelease(event: CdkDragRelease) {}

  drop(event: CdkDragDrop<any[]>) {
    this.disableDrag = false;

    this.pointerDown = undefined;

    // this.pointerDown = this.tiles.map((tile, index) => {
    //   return {imageIndex: tile.dropList[0].number, positionIndex: index};
    // }).filter((tile) => {
    //   return tile.imageIndex === event.container.data[0].number;
    // })[0].positionIndex;

    if (event.previousContainer === event.container) {
      return;
    }

    const temp = event.previousContainer.data[0];
    event.previousContainer.data[0] = event.container.data[0];
    event.container.data[0] = temp;

    this.checkForWin();
  }

  private getIndexForTile(column: number, row: number) {
    return row * this.columns + column;
  }

  /**
   * Shifts all puzzle pieces in the same direction
   */
  shiftUp(event: Event = null) {
    for (let column = 0; column < this.columns; column++) {
      const temp = this.tiles[this.getIndexForTile(column, 0)];
      for (let row = 0; row < this.rows - 1; row++) {
        const currentIndex = this.getIndexForTile(column, row);
        this.tiles[currentIndex] = this.tiles[currentIndex + this.columns];
      }
      this.tiles[this.getIndexForTile(column, this.rows - 1)] = temp;
    }
    this.checkForWin();
    if (event) {
      event.preventDefault();
    }
  }

  /**
   * Shifts all puzzle pieces in the same direction
   */
  shiftRight(event: Event = null) {
    for (let row = 0; row < this.rows; row++) {
      const temp = this.tiles[this.getIndexForTile(this.columns - 1, row)];
      for (let column = 0; column < this.columns - 1; column++) {
        const currentIndex = this.getIndexForTile(this.columns - 1 - column, row);
        this.tiles[currentIndex] = this.tiles[currentIndex - 1];
      }
      this.tiles[this.getIndexForTile(0, row)] = temp;
    }
    this.checkForWin();
    if (event) {
      event.preventDefault();
    }
  }

  /**
   * Shifts all puzzle pieces in the same direction
   */
  shiftDown(event: Event = null) {
    for (let column = 0; column < this.columns; column++) {
      const temp = this.tiles[this.getIndexForTile(column, this.rows - 1)];
      for (let row = 0; row < this.rows - 1; row++) {
        const currentIndex = this.getIndexForTile(column, this.rows - 1 - row);
        this.tiles[currentIndex] = this.tiles[currentIndex - this.columns];
      }
      this.tiles[this.getIndexForTile(column, 0)] = temp;
    }
    this.checkForWin();
    if (event) {
      event.preventDefault();
    }
  }

  /**
   * Shifts all puzzle pieces in the same direction
   */
  shiftLeft(event: Event = null) {
    for (let row = 0; row < this.rows; row++) {
      const temp = this.tiles[this.getIndexForTile(0, row)];
      for (let column = 0; column < this.columns - 1; column++) {
        const currentIndex = this.getIndexForTile(column, row);
        this.tiles[currentIndex] = this.tiles[currentIndex + 1];
      }
      this.tiles[this.getIndexForTile(this.columns - 1, row)] = temp;
    }
    this.checkForWin();
    if (event) {
      event.preventDefault();
    }
  }

  shiftUpPressed() {
    this.framework.buttonBarEmitButton(Button.ImageShiftUp);
  }
  shiftDownPressed() {
    this.framework.buttonBarEmitButton(Button.ImageShiftDown);
  }
  shiftLeftPressed() {
    this.framework.buttonBarEmitButton(Button.ImageShiftLeft);
  }
  shiftRightPressed() {
    this.framework.buttonBarEmitButton(Button.ImageShiftRight);
  }
}
